import React from 'react'
import { registeredMinigridByName } from '../../../minigrid/utils/installationCode'
import { useNavigate } from 'react-router-dom'
import { useQueries, useQuery } from 'react-query'
import { useMeterV2, useAllJoulyMeters, useMeterPoleLinks } from '../../hooks/useMeter'
import MeterV2ListView from './MeterV2ListView'
import Pagination from '../../../../common/components/organisme/Pagination/Pagination'
import { startTransition } from 'react'
import SearchLoader from '../../../../common/components/molecule/Chart/ChartLoaderView/SearchLoader'
import { useSearchContext } from './SearchContext'
import {
  useMeterTagsBulk,
  useMeterTagFilter,
  useUpdateMeterTag,
  useKnownMeterTags,
} from '../../../meterTags/hooks/useMeterTags'
import useMetersTelemetrySnapshot from '../../hooks/useMetersTelemetrySnapshot'
import meterAPI from '../../services/meterAPI'
import minigridAPI from '../../../minigrid/service/minigridAPI'
import { useAuth } from '../../../auth/hooks'

const PAGE_SIZE_OPTIONS = [10, 30, 50]

export default function MeterV2ListPresenter() {
  const { joulyMetersQuery: { data, isLoading, isFetching, isError, error, setPage, setPageSize, pageSize, sreachValue } } = useMeterV2()
  const {
    isTyping,
    setIsTyping,
    selectedTags,
    locationQuery,
    installationCodeQuery,
    relayFilter,
    onlineFilter,
    consumptionSort,
    consumptionMin,
    consumptionMax,
    phoneQuery,
    dateFrom,
    dateTo,
    creditMin,
    creditMax,
    poleFilter,
    gpsFilter,
  } = useSearchContext();
  const { matchingProductNumbers, isActive: tagFilterActive, isLoading: tagFilterLoading } = useMeterTagFilter(selectedTags)

  const minVal = consumptionMin.trim() === '' ? null : Number(consumptionMin)
  const maxVal = consumptionMax.trim() === '' ? null : Number(consumptionMax)
  const minActive = minVal !== null && !Number.isNaN(minVal)
  const maxActive = maxVal !== null && !Number.isNaN(maxVal)

  const creditMinVal = creditMin.trim() === '' ? null : Number(creditMin)
  const creditMaxVal = creditMax.trim() === '' ? null : Number(creditMax)
  const creditMinActive = creditMinVal !== null && !Number.isNaN(creditMinVal)
  const creditMaxActive = creditMaxVal !== null && !Number.isNaN(creditMaxVal)

  const dateFromMs = dateFrom ? new Date(dateFrom).getTime() : NaN
  const dateToMs = dateTo ? new Date(dateTo).getTime() + 24 * 60 * 60 * 1000 - 1 : NaN
  const dateFromActive = !Number.isNaN(dateFromMs)
  const dateToActive = !Number.isNaN(dateToMs)

  const secondaryFiltersActive =
    locationQuery.trim() !== '' ||
    installationCodeQuery.trim() !== '' ||
    relayFilter !== 'ALL' ||
    onlineFilter !== 'ALL' ||
    consumptionSort !== 'NONE' ||
    minActive ||
    maxActive ||
    phoneQuery.trim() !== '' ||
    dateFromActive ||
    dateToActive ||
    creditMinActive ||
    creditMaxActive ||
    poleFilter !== 'ALL' ||
    gpsFilter !== 'ALL'

  const needFullList = tagFilterActive || secondaryFiltersActive
  const allJoulyQuery = useAllJoulyMeters(needFullList)
  const fullListResolving = needFullList && (tagFilterLoading || allJoulyQuery.isLoading)

  const baseList = React.useMemo(() => {
    if (needFullList) return allJoulyQuery.data?.data ?? []
    return data?.data ?? []
  }, [needFullList, allJoulyQuery.data, data?.data])

  const tagAllowedMeters = React.useMemo(() => {
    if (!tagFilterActive) return baseList
    const allowed = new Set(matchingProductNumbers)
    return baseList.filter((m) => allowed.has(m.product_number))
  }, [baseList, tagFilterActive, matchingProductNumbers])

  // Filtre "Minigrid" : résout le nom du LinkedMinigrid (Minigrid inscrit lié
  // à la localisation agrégée) quand disponible, sinon fallback au nom libre
  // stocké côté client. Fetch par compteur uniquement quand le filtre est
  // actif pour éviter N requêtes inutiles.
  // Le filtre « Code installation » lit le code du site inscrit de chaque
  // compteur : il a besoin, comme le filtre Minigrid, de sa localisation.
  const codeFilterActive = installationCodeQuery.trim() !== ''
  const locationFilterActive = locationQuery.trim() !== '' || codeFilterActive
  const gpsFilterActive = gpsFilter !== 'ALL'
  const poleFilterActive = poleFilter !== 'ALL'
  // Le filtre GPS a besoin de la localisation de chaque compteur (comme le filtre
  // Localisation) -> on active le fetch par compteur des que l'un des deux est actif.
  const localizationQueries = useQueries(
    tagAllowedMeters.map((m) => ({
      queryKey: ['meterInfoLocalization', m.product_number] as const,
      queryFn: () => meterAPI.getMeterInfoLocalizationByMeterId(m.product_number),
      enabled: (locationFilterActive || gpsFilterActive) && Boolean(m.product_number),
    }))
  )

  // Liaisons reelles compteur->poteau (1 fetch batch, cache partage avec la colonne
  // "Poteau"). Ensemble des numeros de compteurs relies, pour le filtre Poteau.
  const poleLinksQuery = useMeterPoleLinks()
  const linkedMeterNumbers = React.useMemo(() => {
    const s = new Set<string>()
    for (const x of poleLinksQuery.data ?? []) {
      if (x.meterNumber) s.add(x.meterNumber)
    }
    return s
  }, [poleLinksQuery.data])

  // Presence GPS par numero de compteur (coordonnee canonique non nulle).
  const gpsPresentByPN = React.useMemo(() => {
    const map: Record<string, boolean> = {}
    tagAllowedMeters.forEach((m, idx) => {
      const d = localizationQueries[idx]?.data
      if (!d) return
      map[m.product_number] = (d.lattitude ?? 0) !== 0 || (d.longitude ?? 0) !== 0
    })
    return map
  }, [tagAllowedMeters, localizationQueries])

  // Vrai tant qu'au moins une localisation est en cours de premier chargement.
  // (isLoading && isFetching: en react-query v3, une requête désactivée reste
  // "loading" sans fetcher; on ne compte donc que celles réellement en vol.)
  // Tant que c'est vrai, gpsPresentByPN est incomplet: ne pas appliquer le filtre
  // GPS (sinon liste vide/partielle trompeuse) et afficher le loader.
  const localizationResolving =
    (locationFilterActive || gpsFilterActive) &&
    localizationQueries.some((q) => q.isLoading && q.isFetching)
  const { getAccountIdUser } = useAuth()
  const accountId = getAccountIdUser()
  const allMinigridsQuery = useQuery({
    queryKey: ['allMinigridsByAccount', accountId],
    queryFn: () => minigridAPI.getAllMinigrids(0, 0, { accountId }),
    enabled: Boolean(accountId),
    staleTime: 5 * 60 * 1000,
  })
  // Map lowercase raw name → canonical registered minigrid name. Includes the
  // primary name plus every entry in minigrid_linked_location_names, so one
  // registered Minigrid can match multiple free-text client-info names.
  const registeredNameByLower = React.useMemo(() => {
    const map: Record<string, string> = {}
    for (const mg of allMinigridsQuery.data?.data ?? []) {
      const name = (mg.minigrid_name ?? '').trim()
      if (!name) continue
      map[name.toLowerCase()] = name
      for (const linked of mg.minigrid_linked_location_names ?? []) {
        const lk = (linked ?? '').trim()
        if (lk) map[lk.toLowerCase()] = name
      }
    }
    return map
  }, [allMinigridsQuery.data])

  // Nom libre (minuscules) → code du site inscrit : même table que la colonne
  // « Code installation » (registeredMinigridByName), le code d'un compteur
  // est celui de son site.
  const registeredCodeByLower = React.useMemo(() => {
    const map: Record<string, string> = {}
    const byName = registeredMinigridByName(allMinigridsQuery.data?.data)
    for (const [label, mg] of Object.entries(byName)) {
      const code = (mg.minigrid_installation_code ?? '').trim()
      if (code) map[label] = code
    }
    return map
  }, [allMinigridsQuery.data])

  const installationCodeByPN = React.useMemo(() => {
    const map: Record<string, string> = {}
    tagAllowedMeters.forEach((m, idx) => {
      const raw = localizationQueries[idx]?.data?.minigridName?.trim()
      if (!raw) return
      const code = registeredCodeByLower[raw.toLowerCase()]
      if (code) map[m.product_number] = code
    })
    return map
  }, [tagAllowedMeters, localizationQueries, registeredCodeByLower])

  const minigridNameByPN = React.useMemo(() => {
    const map: Record<string, string> = {}
    tagAllowedMeters.forEach((m, idx) => {
      const raw = localizationQueries[idx]?.data?.minigridName?.trim()
      if (!raw) return
      const matched = registeredNameByLower[raw.toLowerCase()]
      if (!matched) return
      map[m.product_number] = matched
    })
    return map
  }, [tagAllowedMeters, localizationQueries, registeredNameByLower])

  const locationSuggestions = React.useMemo(() => {
    const set = new Set<string>()
    for (const mg of allMinigridsQuery.data?.data ?? []) {
      const n = (mg.minigrid_name ?? '').trim()
      if (n) set.add(n)
    }
    return Array.from(set).sort()
  }, [allMinigridsQuery.data])

  const locationFilteredMeters = React.useMemo(() => {
    const locQ = locationQuery.trim().toLowerCase()
    const codeQ = installationCodeQuery.trim().toUpperCase()
    const phoneQ = phoneQuery.trim().toLowerCase()
    return tagAllowedMeters.filter((m) => {
      if (locQ !== '') {
        const minigridName = (minigridNameByPN[m.product_number] || '').toLowerCase()
        if (!minigridName.includes(locQ)) return false
      }
      if (codeQ !== '') {
        const code = installationCodeByPN[m.product_number] || ''
        if (!code.includes(codeQ)) return false
      }
      if (phoneQ !== '') {
        const phone = (m.clientInfo?.phone || '').toLowerCase()
        if (!phone.includes(phoneQ)) return false
      }
      if (dateFromActive || dateToActive) {
        const rt = m.record_time ? new Date(m.record_time).getTime() : NaN
        if (Number.isNaN(rt)) return false
        if (dateFromActive && rt < dateFromMs) return false
        if (dateToActive && rt > dateToMs) return false
      }
      if (poleFilterActive) {
        const linked = linkedMeterNumbers.has(m.product_number)
        if (poleFilter === 'ON' && !linked) return false
        if (poleFilter === 'OFF' && linked) return false
      }
      if (gpsFilterActive && !localizationResolving) {
        const hasGps = gpsPresentByPN[m.product_number] === true
        if (gpsFilter === 'ON' && !hasGps) return false
        if (gpsFilter === 'OFF' && hasGps) return false
      }
      return true
    })
  }, [tagAllowedMeters, locationQuery, installationCodeQuery, installationCodeByPN, phoneQuery, dateFromActive, dateToActive, dateFromMs, dateToMs, minigridNameByPN, poleFilterActive, poleFilter, linkedMeterNumbers, gpsFilterActive, gpsFilter, gpsPresentByPN, localizationResolving])

  const locationFilteredPNs = React.useMemo(
    () => locationFilteredMeters.map((m) => m.product_number).filter(Boolean),
    [locationFilteredMeters],
  )

  const needTelemetry =
    relayFilter !== 'ALL' ||
    onlineFilter !== 'ALL' ||
    consumptionSort !== 'NONE' ||
    minActive ||
    maxActive ||
    creditMinActive ||
    creditMaxActive
  const snapshotPNs = needTelemetry ? locationFilteredPNs : []
  const { byProductNumber: telemetryByPN } = useMetersTelemetrySnapshot(snapshotPNs)

  const telemetryFilteredMeters = React.useMemo(() => {
    if (!needTelemetry) return locationFilteredMeters
    return locationFilteredMeters.filter((m) => {
      const snap = telemetryByPN[m.product_number]
      if (relayFilter !== 'ALL' && snap?.relayFlag !== relayFilter) return false
      if (onlineFilter !== 'ALL' && snap?.onlineFlag !== onlineFilter) return false
      if (minActive || maxActive) {
        const v = snap?.consumptionIndex
        if (v === null || v === undefined) return false
        if (minActive && v < (minVal as number)) return false
        if (maxActive && v > (maxVal as number)) return false
      }
      if (creditMinActive || creditMaxActive) {
        const v = snap?.credit
        if (v === null || v === undefined) return false
        if (creditMinActive && v < (creditMinVal as number)) return false
        if (creditMaxActive && v > (creditMaxVal as number)) return false
      }
      return true
    })
  }, [locationFilteredMeters, telemetryByPN, needTelemetry, relayFilter, onlineFilter, minActive, maxActive, minVal, maxVal, creditMinActive, creditMaxActive, creditMinVal, creditMaxVal])

  const sortedMeters = React.useMemo(() => {
    if (consumptionSort === 'NONE') return telemetryFilteredMeters
    const dir = consumptionSort === 'ASC' ? 1 : -1
    const withValue = telemetryFilteredMeters
      .map((m) => ({ m, v: telemetryByPN[m.product_number]?.consumptionIndex ?? null }))
    withValue.sort((a, b) => {
      if (a.v === null && b.v === null) return 0
      if (a.v === null) return 1
      if (b.v === null) return -1
      return (a.v - b.v) * dir
    })
    return withValue.map((x) => x.m)
  }, [telemetryFilteredMeters, consumptionSort, telemetryByPN])

  const effectivePageSize = pageSize || 10
  const [clientPage, setClientPage] = React.useState(1)
  const clientTotalPages = Math.max(1, Math.ceil(sortedMeters.length / effectivePageSize))

  const selectedTagsKey = selectedTags.join(',')
  React.useEffect(() => {
    setClientPage(1)
  }, [
    tagFilterActive,
    locationQuery,
    installationCodeQuery,
    relayFilter,
    onlineFilter,
    consumptionSort,
    consumptionMin,
    consumptionMax,
    phoneQuery,
    dateFrom,
    dateTo,
    creditMin,
    creditMax,
    poleFilter,
    gpsFilter,
    selectedTagsKey,
    effectivePageSize,
  ])

  React.useEffect(() => {
    if (clientPage > clientTotalPages) setClientPage(clientTotalPages)
  }, [clientPage, clientTotalPages])

  const visibleMeters = React.useMemo(() => {
    if (!needFullList) return sortedMeters
    const start = (clientPage - 1) * effectivePageSize
    return sortedMeters.slice(start, start + effectivePageSize)
  }, [sortedMeters, needFullList, clientPage, effectivePageSize])

  const visibleProductNumbers = React.useMemo(
    () => visibleMeters.map((m) => m.product_number).filter(Boolean),
    [visibleMeters],
  )
  const { byProductNumber } = useMeterTagsBulk(visibleProductNumbers)
  const updateTagMutation = useUpdateMeterTag()
  const knownTags = useKnownMeterTags()

  // Désactiver isTyping quand la requête est réellement envoyée
  React.useEffect(() => {
    if (isFetching) {
      setIsTyping(false);
    }
  }, [isFetching, setIsTyping]);

  // Détecter si une recherche est en cours (soit en train de taper, soit en train de fetch)
  // Le loader s'affiche immédiatement si isTyping est true, ou si on fetch avec une valeur de recherche
  const isSearching = Boolean(isTyping || (sreachValue && sreachValue.trim() !== '' && isFetching))

  const navigate = useNavigate()

  const goToSingleMeter = (meterId: string) => {
    navigate(`/main/meters/${meterId}?device_name=${meterId}`)
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value)
    setPageSize(value) // setPageSize réinitialise automatiquement la page à 1
  }

  const handlePageChange = (page: number) => {
    if (!isFetching) {
      // Utiliser startTransition pour rendre le changement de page non-bloquant
      startTransition(() => {
        setPage(page)
      })
    }
  }

  if (isError) {
    return <div>{error?.message || 'Une erreur est survenue'} status {error?.response?.status}</div>
  }

  return (
    <>
      {isSearching ? (
        <SearchLoader searchTerm={sreachValue || undefined} />
      ) : (
        <>
          <MeterV2ListView
            meterList={visibleMeters}
            goToSingleMeter={goToSingleMeter}
            isLoading={isLoading || fullListResolving || localizationResolving}
            searchTerm={sreachValue}
            tagsByProductNumber={byProductNumber}
            onTagSave={(productNumber, nextTags) =>
              updateTagMutation.mutateAsync({ productNumber, tags: nextTags })
            }
            tagSuggestions={knownTags}
            locationSuggestions={locationSuggestions}
          />
        </>
      )}
      {!isSearching && needFullList && (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', marginBottom: '0.5rem' }}>
            <label style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Nombre d'éléments par page:
              <select
                value={effectivePageSize}
                onChange={handlePageSizeChange}
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={{ textAlign: 'center', marginTop: '0.5rem', marginBottom: '0.5rem', color: '#666' }}>
            Page {clientPage} sur {clientTotalPages}
            {` (${sortedMeters.length} élément${sortedMeters.length > 1 ? 's' : ''} filtré${sortedMeters.length > 1 ? 's' : ''})`}
          </div>
          {clientTotalPages > 1 && (
            <Pagination
              centered
              currentPage={clientPage}
              lastPage={clientTotalPages}
              setCurrentPage={setClientPage}
            />
          )}
        </>
      )}
      {!isSearching && !needFullList && data?.pagination && (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', marginBottom: '0.5rem' }}>
            <label style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Nombre d'éléments par page:
              <select
                value={pageSize || 10}
                onChange={handlePageSizeChange}
                disabled={isFetching}
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: isFetching ? 'not-allowed' : 'pointer',
                  opacity: isFetching ? 0.6 : 1
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {data.pagination.total_pages > 1 && (
            <>
              <div style={{ textAlign: 'center', marginTop: '0.5rem', marginBottom: '0.5rem', color: '#666' }}>
                Page {data.pagination.current_page} sur {data.pagination.total_pages}
                {data.pagination.total_count && ` (${data.pagination.total_count} éléments au total)`}
                {isFetching && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#999' }}>Chargement...</span>}
              </div>
              <div style={{ pointerEvents: isFetching ? 'none' : 'auto', opacity: isFetching ? 0.6 : 1 }}>
                <Pagination
                  centered
                  currentPage={data.pagination.current_page}
                  lastPage={data.pagination.total_pages}
                  setCurrentPage={handlePageChange}
                />
              </div>
            </>
          )}
          {data.pagination.total_pages <= 1 && data.pagination.total_count > 0 && (
            <div style={{ textAlign: 'center', marginTop: '0.5rem', color: '#666' }}>
              {data.pagination.total_count} élément{data.pagination.total_count > 1 ? 's' : ''} au total
            </div>
          )}
        </>
      )}

    </>
  )
}
